import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Pin, Save, Search as SearchIcon, Sparkles, X } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import { useT } from "../../hooks/useT";
import { useTelemetry } from "../../hooks/useTelemetry";
import { api } from "../../lib/api";
import { useUI } from "../../stores/ui";
import { AddToNotebookPopover } from "../notebooks/AddToNotebookPopover";
import { EMPTY_FACETS, FacetRail, type Facets } from "./FacetRail";
import { addSavedSearch, useSavedSearches } from "./useSavedSearches";

type GroupBy = "none" | "doc" | "collection";

const FACET_LABELS: Record<keyof Facets, string> = {
  collections: "Colección",
  projects: "Proyecto",
  kinds: "Tipo",
  langs: "Idioma",
  status: "Estado",
  tags: "Etiqueta",
};

export function SearchPage() {
  const t = useT();
  const [query, setQuery] = useState("");
  const [facets, setFacets] = useState<Facets>(EMPTY_FACETS);
  const [groupBy, setGroupBy] = useState<GroupBy>("none");
  const setChatDraft = useUI((s) => s.setChatDraft);
  const setChatPanel = useUI((s) => s.setChatPanel);
  const setScope = useUI((s) => s.setScope);
  const { items: saved } = useSavedSearches();
  const track = useTelemetry();

  const search = useMutation({
    mutationFn: (q: string) =>
      api.search({
        query: q,
        filters: {
          projectIds:
            facets.collections.length > 0
              ? facets.collections
              : facets.projects.length > 0
                ? facets.projects
                : undefined,
          kinds: facets.kinds.length > 0 ? facets.kinds : undefined,
          status: facets.status.length > 0 ? facets.status : undefined,
          tags: facets.tags.length > 0 ? facets.tags : undefined,
        },
      }),
  });

  function run(q: string) {
    if (!q.trim()) return;
    search.mutate(q.trim());
  }

  function removeChip(group: keyof Facets, value: string) {
    setFacets({ ...facets, [group]: facets[group].filter((v) => v !== value) });
  }

  function clearAll() {
    setFacets(EMPTY_FACETS);
  }

  function saveCurrent() {
    if (!query.trim()) {
      toast.error("Escribe una búsqueda primero");
      return;
    }
    addSavedSearch({ query: query.trim(), facets });
    toast.success("Búsqueda guardada");
    track("search_save", {
      scope: "all",
      filters_count: Object.values(facets).reduce((a, b) => a + b.length, 0),
      has_query: true,
    });
  }

  function askAboutResults() {
    if (!search.data || search.data.items.length === 0) return;
    const ids = search.data.items
      .map((i) => i.id)
      .slice(0, 20)
      .join(", ");
    setChatDraft(`Sobre estos ${search.data.items.length} resultados (${ids}…): ${query}`);
    setScope("all");
    setChatPanel("open");
    toast.message("ChatRail abierto con scope sobre los resultados");
    track("search_ask_in_results", {
      n_results: search.data.items.length,
      scope: "all",
    });
  }

  const activeChips = useMemo(() => {
    const chips: Array<{ group: keyof Facets; value: string }> = [];
    for (const group of Object.keys(facets) as (keyof Facets)[]) {
      for (const v of facets[group]) chips.push({ group, value: v });
    }
    return chips;
  }, [facets]);

  const groupedResults = useMemo(() => {
    if (!search.data) return [];
    const items = search.data.items;
    if (groupBy === "none") return [{ key: "all", title: null as string | null, items }];
    const map = new Map<string, typeof items>();
    for (const it of items) {
      const key =
        groupBy === "doc"
          ? (it.title ?? it.path)
          : ((it as { collection_title?: string }).collection_title ?? "Sin colección");
      const arr = map.get(key) ?? [];
      arr.push(it);
      map.set(key, arr);
    }
    return Array.from(map.entries()).map(([title, list]) => ({
      key: title,
      title,
      items: list,
    }));
  }, [search.data, groupBy]);

  return (
    <div className="flex h-full overflow-hidden">
      <aside
        data-testid="facet-rail-aside"
        className="w-60 overflow-y-auto border-r border-(--border-subtle) bg-(--bg-elevated) px-3 py-4"
      >
        <FacetRail
          facets={facets}
          onChange={setFacets}
          dynamicCounts={(search.data?.facets as Record<string, Record<string, number>>) ?? {}}
        />
      </aside>

      <main className="flex-1 overflow-y-auto px-6 py-6">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            run(query);
          }}
          className="mx-auto mb-4 max-w-4xl"
        >
          <div
            className="flex items-center gap-2 rounded-lg border border-(--border-subtle) bg-(--bg-elevated) px-3 py-2 shadow-(--elev-1)"
            data-testid="search-input-wrap"
          >
            <SearchIcon size={16} className="text-(--text-tertiary)" />
            <Input
              placeholder={t("search.placeholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0"
              data-testid="search-input"
            />
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={search.isPending}
              data-testid="search-submit"
            >
              <SearchIcon size={13} /> Buscar
            </Button>
          </div>

          {/* B4.2 — Active chips bar */}
          {activeChips.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="active-chips">
              {activeChips.map((c) => (
                <ChipPill
                  key={`${c.group}:${c.value}`}
                  group={FACET_LABELS[c.group]}
                  value={c.value}
                  onRemove={() => removeChip(c.group, c.value)}
                />
              ))}
              <button
                type="button"
                onClick={clearAll}
                data-testid="active-chips-clear"
                className="ml-1 text-xs text-(--accent-600) hover:underline"
              >
                limpiar todo
              </button>
            </div>
          )}

          {search.data && (
            <div
              className="mt-3 flex flex-wrap items-center gap-3 text-xs text-(--text-tertiary)"
              data-testid="search-summary"
            >
              <span>
                <strong className="text-(--text-primary)">{search.data.items.length}</strong>{" "}
                resultados
              </span>
              <span>·</span>
              <span>scoring: bm25 + vector</span>

              {/* B4.3 — Group-by */}
              <span className="ml-auto inline-flex items-center gap-1.5">
                <span>agrupar por</span>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value as GroupBy)}
                  data-testid="search-group-by"
                  className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2 py-0.5 text-xs"
                >
                  <option value="none">— ninguno —</option>
                  <option value="doc">documento</option>
                  <option value="collection">colección</option>
                </select>
              </span>

              {/* B4.4 — Save search */}
              <Button
                variant="ghost"
                size="sm"
                onClick={saveCurrent}
                data-testid="search-save"
                title="Guardar búsqueda"
              >
                <Save size={12} />
              </Button>
            </div>
          )}
        </form>

        <div className="mx-auto max-w-4xl">
          {search.isPending && (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          )}

          {search.data && search.data.items.length === 0 && (
            <div className="rounded-lg border border-dashed border-(--border-strong) p-8 text-center text-(--text-tertiary)">
              {t("search.empty")}
            </div>
          )}

          {groupedResults.map((g) => (
            <section key={g.key} data-testid="search-group">
              {g.title && (
                <header
                  className="mt-4 mb-2 flex items-center gap-2 border-b border-(--border-subtle) pb-1 text-xs text-(--text-secondary)"
                  data-testid="search-group-head"
                >
                  <strong className="text-(--text-primary)">{g.title}</strong>
                  <span className="text-(--text-tertiary)">· {g.items.length} resultados</span>
                </header>
              )}
              <ul className="space-y-2">
                {g.items.map((item) => (
                  <li
                    key={item.id}
                    className="rounded-lg border border-(--border-subtle) bg-(--bg-elevated) p-4 hover:bg-(--bg-hover)"
                    data-testid="search-result"
                  >
                    <header className="text-xs text-(--text-tertiary)">
                      {item.path}
                      {item.heading_path.length > 0 && <> · § {item.heading_path.join(" › ")}</>}
                    </header>
                    <div className="mt-1 flex items-baseline gap-2">
                      <h3 className="text-base font-semibold">
                        <Link to="/docs/$docId" params={{ docId: item.doc_id }}>
                          {item.title}
                        </Link>
                      </h3>
                      <Badge variant="muted" className="text-[10px]">
                        score {item.score.toFixed(2)}
                      </Badge>
                      <Badge variant="outline" className="text-[10px]">
                        {item.reason}
                      </Badge>
                    </div>
                    <p className="mt-1 text-sm text-(--text-secondary) line-clamp-3">
                      {item.excerpt}
                    </p>
                    <div className="mt-2 flex gap-1">
                      <Button variant="ghost" size="sm" asChild>
                        <Link to="/docs/$docId" params={{ docId: item.doc_id }}>
                          Abrir
                        </Link>
                      </Button>
                      <AddToNotebookPopover
                        item={{
                          item_type: "doc",
                          source_doc_id: item.doc_id,
                          source_section_id: item.section_id ?? undefined,
                          title: item.title,
                        }}
                        trigger={
                          <Button variant="ghost" size="sm">
                            📓 Apuntes
                          </Button>
                        }
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard
                            .writeText(`[sec:${item.section_id ?? item.id}]`)
                            .catch(() => {});
                          toast.success("Cita copiada");
                        }}
                      >
                        Copiar cita
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          {/* B4.5 — Ask about results banner */}
          {search.data && search.data.items.length > 0 && (
            <div
              data-testid="ask-about-results"
              className="mt-6 flex items-center gap-3 rounded-lg border border-(--accent-200) bg-(--accent-50) p-4"
            >
              <Sparkles size={16} className="text-(--accent-500)" />
              <div className="flex-1">
                <p className="text-sm font-medium text-(--accent-700)">
                  Preguntar sobre estos {search.data.items.length} resultados
                </p>
                <p className="text-xs text-(--text-secondary)">
                  El chat usará solo estas fuentes y las citará limitadas a este conjunto.
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={askAboutResults}
                data-testid="ask-about-results-cta"
              >
                <Sparkles size={13} /> Abrir chat
              </Button>
            </div>
          )}

          {/* B4.4 — Saved searches list */}
          {saved.length > 0 && (
            <section
              className="mt-8 rounded-lg border border-(--border-subtle) bg-(--bg-canvas) p-4"
              data-testid="saved-searches"
            >
              <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-(--text-tertiary)">
                <Pin size={11} /> Búsquedas guardadas
              </h3>
              <ul className="space-y-1">
                {saved.slice(0, 5).map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setQuery(s.query);
                        setFacets(s.facets);
                        run(s.query);
                      }}
                      data-testid="saved-search-item"
                      className="flex w-full items-center justify-between rounded px-2 py-1 text-sm hover:bg-(--bg-hover)"
                    >
                      <span className="truncate">{s.query}</span>
                      <span className="ml-2 font-mono text-[10px] text-(--text-tertiary)">
                        {new Date(s.created_at).toLocaleDateString("es-ES")}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </main>
    </div>
  );
}

function ChipPill({
  group,
  value,
  onRemove,
}: {
  group: string;
  value: string;
  onRemove: () => void;
}) {
  return (
    <span
      data-testid="active-chip"
      className="inline-flex items-center gap-1 rounded-full bg-(--accent-50) border border-(--accent-100) px-2.5 py-1 text-[11px] text-(--accent-700)"
    >
      <span className="font-mono uppercase tracking-wider text-(--text-tertiary)">{group}:</span>
      <span className="font-medium">{value}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Quitar filtro ${group}:${value}`}
        data-testid="active-chip-remove"
        className="ml-0.5 rounded-full p-0.5 hover:bg-(--accent-100)"
      >
        <X size={10} />
      </button>
    </span>
  );
}
